import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { useNavbarLogo } from "@/contexts/NavbarLogoContext";

interface NavbarProps {
  isOpen: boolean;
  toggle: () => void;
  isHomePage?: boolean;
}

// Logo servi en local pour éviter de consommer la bande passante Supabase Storage
const SUPABASE_LOGO_URL = "/images/logo-bbz-sans-baseline.png";
const Navbar = ({
  isOpen,
  toggle,
  isHomePage = false
}: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const { heroLogoVisible } = useNavbarLogo();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'contain';
    } else {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isOpen]);
  return <>
      <header className={`fixed w-full transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-sm shadow-md py-2" : "bg-transparent py-4"} z-30`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <NavLink to="/" className="flex items-center" aria-label="Page d'accueil de Bio Beauté Zen" title="Retour à l'accueil - Bio Beauté Zen institut de beauté à Montpellier">
            <img 
              src={SUPABASE_LOGO_URL} 
              alt="Logo officiel de Bio Beauté Zen, institut de beauté bio et bien-être à Montpellier" 
              loading="eager" 
              className={`max-h-7 w-auto transition-opacity duration-300 ${
                isHomePage && heroLogoVisible ? "opacity-0" : "opacity-100"
              }`}
            />
          </NavLink>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-bbz-text p-2 z-50 relative" onClick={toggle} aria-label="Ouvrir ou fermer le menu de navigation mobile">
            {isOpen ? <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>}
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center" aria-label="Navigation principale">
            <div className="flex space-x-1 lg:space-x-6">
              <NavLink to="/" title="Accueil - Institut Bio Beauté Zen à Montpellier" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>
                Accueil
              </NavLink>
              <NavLink to="/institut-beaute-bio-montpellier" title="Présentation de Bio Beauté Zen" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>L'institut</NavLink>
              <NavLink to="/soins-visage-massages-epilations-montpellier" title="Carte des soins bio et naturels" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>
                Carte des soins
              </NavLink>
              <NavLink to="/bon-cadeaux-estheticienne-bio-montpellier" title="Offrir un bon cadeau bien-être" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>
                Bon Cadeau
              </NavLink>
              <NavLink to="/contact-institut-montpellier" title="Nous contacter - Bio Beauté Zen" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>
                Contact
              </NavLink>
              <NavLink to="/questions-frequentes-institut-beaute" title="Foire aux questions sur Bio Beauté Zen" className={({
              isActive
            }) => isActive ? "font-medium text-bbz-text border-b-2 border-bbz-green px-1 py-1 whitespace-nowrap" : "font-medium text-bbz-text hover:text-bbz-green transition-colors px-1 py-1 whitespace-nowrap"}>
                FAQ
              </NavLink>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Navigation Portal */}
      {isOpen && createPortal(<div className="md:hidden fixed inset-0 z-40 bg-white/40 backdrop-blur-lg will-change-transform" style={{
      transform: 'translate3d(0,0,0)',
      overscrollBehavior: 'contain'
    }} role="dialog" aria-modal="true" aria-label="Menu mobile">
          {/* Close button */}
          <button className="absolute top-4 right-4 text-bbz-text p-2 hover:text-bbz-green transition-colors z-50" onClick={toggle} aria-label="Fermer le menu mobile">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Navigation using CSS Grid for perfect centering */}
          <nav className="h-full grid place-items-center p-4">
            <div className="flex flex-col items-center space-y-8">
              <NavLink to="/" title="Accueil - Institut Bio Beauté Zen" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                Accueil
              </NavLink>
              <NavLink to="/institut-beaute-bio-montpellier" title="En savoir plus sur Bio Beauté Zen" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                Bio Beauté Zen
              </NavLink>
              <NavLink to="/soins-visage-massages-epilations-montpellier" title="Voir la carte des soins" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                Carte des soins
              </NavLink>
              <NavLink to="/bon-cadeaux-estheticienne-bio-montpellier" title="Offrir un bon cadeau" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                Bon Cadeau
              </NavLink>
              <NavLink to="/contact-institut-montpellier" title="Contacter l'institut" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                Contact
              </NavLink>
              <NavLink to="/questions-frequentes-institut-beaute" title="FAQ - Questions fréquentes" className="font-medium text-xl text-bbz-text hover:text-bbz-green transition-colors" onClick={toggle}>
                FAQ
              </NavLink>
            </div>
          </nav>
        </div>, document.body)}
    </>;
};
export default Navbar;